import {
  FC,
  useCallback,
  useMemo,
} from 'react';
import { Field, useFormState, type FieldInputProps } from 'react-final-form';
import { DURATION_FIELD, type BookNowDirectFormState } from 'src/book-now-direct/constants';
import Dropdown, { DropdownOption } from 'src/common/components/DropdownDS22';
import { useTranslation } from 'next-i18next';
import Label from 'src/common/components/Label';
import { useBookNowDirectContext } from 'src/book-now-direct/providers/BookNowDirectProvider';
import styles from './DurationField.module.scss';

const DurationField: FC = () => {
  const { allInstabooksOnDate } = useBookNowDirectContext();
  const { values: { duration } } = useFormState<BookNowDirectFormState>();
  const [tCommon] = useTranslation('common');
  const [tDirect] = useTranslation('book-now-direct');

  const options: DropdownOption<string>[] = useMemo(() => {
    if (!allInstabooksOnDate) {
      return [];
    }
    const durationOptions = allInstabooksOnDate.map(({ trip_length }) => {
      const hours = Number(trip_length.split(':')[0]);
      return {
        hours,
        value: trip_length,
        id: trip_length,
        dropdownLabel: tDirect('duration', { count: Number(hours) }),
      };
    });
    // uniqueBy hours property. Using Map characteristic of unique keys.
    return Array.from(
      new Map(durationOptions.map((obj) => [obj.hours, obj])).values(),
    ).sort((a, b) => a.hours - b.hours);
  }, [allInstabooksOnDate, tDirect]);

  const selectedOption = options?.find(({ id }) => id === duration) ?? options[0];

  const onChange = useCallback((
    input: FieldInputProps<DropdownOption, HTMLElement>,
  ) => (option: DropdownOption) => {
    input.onChange(option.id);
  }, []);

  return (
    <div className={styles.root}>
      <Label
        htmlFor={DURATION_FIELD}
        label={tCommon('trip-details--duration')}
        size="m"
      />
      <Field name={DURATION_FIELD}>
        {({ input, meta }) => (
          <Dropdown
            id="book-now-direct-duration"
            selectedValue={selectedOption || null}
            variant="white"
            options={options}
            fullWidth
            limitMenuHeight
            onChange={onChange(input)}
            buttonSize="l"
            optionSize="m"
            data-test="duration"
            selectedIconId="duration"
            selectedIconSize="l"
            error={meta.submitFailed && meta.error}
          />
        )}
      </Field>
    </div>
  );
};

export default DurationField;
